import {
  AllActivityTypesField,
  CreateScene,
  EntityAction,
  EntityFormActions,
  EntityScene,
  Heading,
  IndexScene,
  ModelScene,
  PermissionDenied,
  UpdateScene,
  useEntity,
} from 'components';
import { TableFilter } from 'components/ControlledTable';
import { useNavigate, useRoutes } from 'react-router';
import { Box, Button, LoadingContainer, Stack } from '@ff-it/ui';
import type { Vendor } from 'modules/supplier/vendor/types';
import { TextField } from '@ff-it/form';
import { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import { OutletGrid } from 'modules/supplier/outletOrServiceCategory/OutletGrid';
import { OutletNav } from './Nav';
import permissions from 'modules/supplier/vendor/permissions';
import { VendorOutletProducts } from './VendorOutletProducts';
import { OutletOrServiceCategoryForm } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryForm';
import { useHasPerms } from 'core/permissions';
import { Link } from 'react-router-dom';
import { ReactElement } from 'react';
import { openCatalogAction } from 'components/CatalogAction';
import { useSessionScope } from 'services';

const initialValues = {
  name: '',
  kind: 'OUTLET',
  type: null,
  category: null,
  language: null,
};

function OutletActions(): ReactElement {
  const ctx = useEntity();
  const navigate = useNavigate();
  const { slug } = useSessionScope();

  const actions: EntityAction<any>[] = [
    {
      action: 'catalog',
      permKey: 'change',
      button: {
        icon: 'list-tree',
        children: 'Catalog',
        variant: 'secondary',
        size: 'sm',
        onClick: () =>
          openCatalogAction(ctx, (s) => {
            // dismissed
            if (!s) {
              return;
            }
            if (s.action === 'MOVE') {
              const vendor = s.target as Vendor;
              navigate(`/${slug}/preferences/vendors/${vendor.id}/outlets/${s.id}`);
              return;
            }

            if (s.action === 'REMOVE') {
              if (!s.cascade) {
                navigate(`../`);
              } else {
                // We don't want to backtrack for now
                navigate(`/${slug}/preferences`);
              }
              return;
            }

            throw new Error(`Action not handled: ${s.action}`);
          }),
      },
    },
  ];

  return <EntityFormActions ml="5" actions={actions} />;
}

export function VendorOutlets() {
  const { item: vendor, endpoint } = useEntity<Vendor>();
  const url = `${endpoint}outlets/`;

  const [hasIndex, hasCreate] = useHasPerms(permissions?.view, permissions?.add);

  const routes = useRoutes([
    {
      index: true,
      element: (
        <IndexScene
          columns={[]}
          pageSize={Number.POSITIVE_INFINITY}
          renderTable={({ data, isLoading }) => {
            return (
              <LoadingContainer loading={isLoading}>
                <Heading title="Outlets">
                  <Stack direction="horizontal">
                    {hasCreate && (
                      <Button asChild variant="primary" size="sm" icon="circle-plus">
                        <Link to="create">Create</Link>
                      </Button>
                    )}
                  </Stack>
                </Heading>
                <Box display="flex" mt="2">
                  <Box>
                    <TableFilter>
                      <TextField name="search" placeholder="Search.." size="sm" />
                      <AllActivityTypesField name="type" placeholder="Type" className="ml-1" size="sm" />
                    </TableFilter>
                  </Box>
                </Box>
                {data && <OutletGrid outlets={data.rows} />}
              </LoadingContainer>
            );
          }}
        />
      ),
    },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues}>
          <OutletOrServiceCategoryForm />
        </CreateScene>
      ),
    },
    {
      path: ':id/*',
      element: (
        <EntityScene>
          <OutletNav vendor={vendor} />
        </EntityScene>
      ),
      children: [
        {
          path: 'products/*',
          element: <VendorOutletProducts />,
        },

        {
          index: true,
          element: (
            <UpdateScene viewDisabled heading={null} deletePath={null} actions={<OutletActions />}>
              <OutletOrServiceCategoryForm />
            </UpdateScene>
          ),
        },
      ],
    },
  ]);

  if (!hasIndex) {
    return <PermissionDenied />;
  }

  return (
    <ModelScene
      endpoint={url}
      title="Outlet"
      pluralTitle="Outlets"
      entityTitle={({ name }: Outlet) => name}
      permissions={permissions}
    >
      {routes}
    </ModelScene>
  );
}
